import { Fragment, useEffect, useState } from 'react';

import { Combobox } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconZoomOutline } from '@iwoca/icons';
import {
  GetStateResponse,
  fetchPutState,
  fetchGetPostcodeLookup,
  GetPostcodeLookupResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Input as OrionInput } from '@iwoca/orion';
import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { cloneDeep, merge, set } from 'lodash-es';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import {
  denormalisePerson,
  normalizePerson,
  normalizeAddress,
} from './normalisation';
import PlusIcon from './svg/PlusIcon.svg';
import { shareholderValidator } from './validation';
import {
  useGetFundingRequirement,
  useGetStateByStateKey,
} from '../../../../api/lending/lapiHooks';
import { Accordion } from '../../../../Buyer/components/Accordion/Accordion';
import { HookFormDropDown } from '../../../../components/Dropdown/Dropdown';
import { formatDate } from '../../../../components/Input/formatDate';
import { HookFormInput } from '../../../../components/Input/Input';
import { LoadingSpinner } from '../../../../components/LoadingSpinner/LoadingSpinner';
import { useStateKey } from '../../../../hooks/useStateKey.hook';
import { Card } from '../../../components/Card/Card';
import { InlineButton } from '../../../components/InlineButton/InlineButton';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { Divider } from '../../components/Divider/Divider';
import { useNavigateToNextRequirement } from '../../hooks/useNavigateToNextRequirement';

export const MajorShareholders = () => {
  const { goToNextRequirement, nextRequirementName } =
    useNavigateToNextRequirement();
  const { fetchFundingRequirements } = useGetFundingRequirement();

  const { state, loadingState, refetchState } = useGetStateByStateKey();
  const { stateKey } = useStateKey();

  const [currentlyEditingIndex, setEditingIndex] = useState<number | null>(
    null,
  );
  const [showAdding, setShowAdding] = useState(false);

  // TODO: figure out how to replace this with the `isLoading` flag from react-query
  const [submittingShareholder, setSubmittingShareholder] = useState(false);

  const onClickOption = (editingIndex?: number) => {
    if (!editingIndex) {
      setEditingIndex(null);
      setShowAdding(true);
      return;
    }

    setEditingIndex(editingIndex);
    setShowAdding(false);
  };

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (state: GetStateResponse) => {
      if (!stateKey) {
        throw new Error('No statekey provided');
      }

      await fetchPutState({ stateKey, body: state });

      await refetchState();
    },
  });

  const removeDirector = async (uid: string) => {
    if (!state) {
      throw new Error('No previous customer state found');
    }

    const newState = cloneDeep(state);

    const people = newState?.application?.people?.filter(
      (person) => person.uid !== uid,
    );

    set(newState, 'application.people', people);
    await mutateAsync({ data: newState });
    await fetchFundingRequirements();
  };

  const onSubmit = async (values: TPersonForm) => {
    if (!state) {
      throw new Error('No previous customer state found');
    }

    setSubmittingShareholder(true);

    const newState = cloneDeep(state);
    const newApplicant =
      newState.application?.people?.find(
        (person) => person.uid === values.uid,
      ) || {};

    const applicantState = denormalisePerson(values);
    merge(newApplicant, applicantState);

    const existingPersonIndex = state.application!.people!.findIndex(
      (person) => person.uid === values.uid,
    );

    if (existingPersonIndex >= 0) {
      set(newState, `application.people[${existingPersonIndex}]`, newApplicant);
    } else {
      const existingPeople = state!.application!.people || [];
      set(newState, 'application.people', [...existingPeople, newApplicant]);
    }

    await mutateAsync({ data: newState });
    await fetchFundingRequirements();

    setShowAdding(false);
    setSubmittingShareholder(false);
  };

  const people = state?.application?.people || [];
  const addedShareholders = people.filter(
    (person) =>
      person.roles?.includes('shareholder') &&
      !person.roles.includes('applicant'),
  );

  const isSubmitButtonDisabled =
    isPending || !addedShareholders.length || submittingShareholder;

  if (loadingState || !state) return <>Loading...</>;

  return (
    <Card title="Major shareholders info">
      <p className="font-reg text-primary-10">
        All the directors and shareholders that own 25% or more of your
        business.
      </p>
      <Accordion title="We need this to protect against fraud">
        <br></br>
        We’ll use this info for fraud and anti money laundering checks on all
        major shareholders.
      </Accordion>
      <div className="grid gap-l">
        {state.application?.people?.map((person, index) => (
          <DirectorCard
            key={person.uid}
            person={normalizePerson(person)}
            onSubmit={async (values) => {
              await onSubmit(values);
              setEditingIndex(null);
            }}
            removeDirector={removeDirector}
            onClickEdit={() => onClickOption(index)}
            showEditing={index === currentlyEditingIndex}
            loading={isPending}
          />
        ))}
      </div>
      <AddDirector
        onClickAdd={onClickOption}
        showAdding={showAdding}
        // Needs fixing. Types are not 100% identical
        // @ts-expect-error
        onSubmit={onSubmit}
        loading={isPending}
      />
      <Divider />
      <p>Are you finished adding all your business’ major shareholders?</p>

      <WhiteLabelOverride
        componentType="button"
        disabled={isSubmitButtonDisabled}
      >
        <Button
          disabled={isSubmitButtonDisabled}
          onClick={goToNextRequirement}
          loading={submittingShareholder}
          className="sm:w-full"
        >
          Continue to {nextRequirementName}
        </Button>
      </WhiteLabelOverride>
    </Card>
  );
};

type TPersonForm = ReturnType<typeof normalizePerson>;

const TitleOptions = () => (
  <>
    <option value=""></option>
    <option value="mr">Mr</option>
    <option value="ms">Ms</option>
    <option value="mrs">Mrs</option>
    <option value="miss">Miss</option>
    <option value="sir">Sir</option>
    <option value="professor">Professor</option>
    <option value="doctor">Doctor</option>
    <option value="lord">Lord</option>
    <option value="lady">Lady</option>
    <option value="baron">Baron</option>
    <option value="baroness">Baroness</option>
    <option value="rev">Reverend</option>
  </>
);

const EditDirectorForm = ({
  person,
  onSubmit,
  loading,
}: {
  person: TPersonForm;
  onSubmit: (values: TPersonForm) => void;
  loading: boolean;
}) => {
  const methods = useForm({
    defaultValues: person,
    // @ts-expect-error - validator's Yup type is stricter than the defaults
    resolver: yupResolver(shareholderValidator),
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          onSubmit as Parameters<typeof methods.handleSubmit>[0],
        )}
      >
        <HookFormInput type="hidden" name="uid" />
        <HookFormDropDown name="title" labelText="Title">
          <TitleOptions />
        </HookFormDropDown>
        <HookFormInput name="firstName" label="First name" type="text" />
        <HookFormInput name="lastName" label="Last name" type="text" />
        <HookFormInput
          name="dateOfBirth"
          label="Date of birth (DD/MM/YYYY)"
          placeholder="DD/MM/YYYY"
          formatter={formatDate}
          inputMode="numeric"
        />
        <AddressInput />
        <Button type="submit" variant="primary">
          {loading ? <LoadingSpinner /> : 'Save'}
        </Button>
      </form>
    </FormProvider>
  );
};

const DirectorCard = ({
  person,
  onSubmit,
  removeDirector,
  onClickEdit,
  showEditing = false,
  loading = false,
}: {
  person: TPersonForm;
  onSubmit: (values: TPersonForm) => void;
  removeDirector: (uid: string) => Promise<void>;
  onClickEdit: () => void;
  showEditing: boolean;
  loading: boolean;
}) => {
  const isPersonApplicant = person.roles.includes('applicant');

  if (showEditing) {
    return (
      <div className="mt-[28px] border-t border-structure-80">
        <h3>Edit major shareholder</h3>
        <EditDirectorForm
          person={person}
          onSubmit={onSubmit}
          loading={loading}
        />
      </div>
    );
  }

  return (
    <div className="rounded-s border border-structure-80 p-l">
      <div className="grid gap-r">
        {isPersonApplicant ? (
          <div>
            <span className="inline-block rounded-2xl bg-secondary-40 px-s text-s font-bold not-italic leading-[20px] text-white">
              <b>This is you</b>
            </span>
          </div>
        ) : null}
        <div className="fs-mask text-s font-med leading-[20px] text-primary-10">
          {person.firstName} {person.lastName}
        </div>
        <div className="fs-mask text-s font-med leading-[20px] text-primary-10">
          {person.dateOfBirth}
        </div>
        <div className="fs-mask text-s font-med leading-[20px] text-primary-10">
          {normalizeAddress(person.address)}
        </div>
      </div>
      {!isPersonApplicant && (
        <div className="mt-xl flex justify-between border-t border-structure-80 pt-r">
          <InlineButton onClick={onClickEdit}>Edit</InlineButton>
          <InlineButton
            className="text-error"
            onClick={() => removeDirector(person.uid)}
          >
            Remove
          </InlineButton>
        </div>
      )}
    </div>
  );
};

const DIRECTOR_INTIAL_VALUES = {
  title: '',
  firstName: '',
  lastName: '',
  address: {
    manual: false,
    streetName: '',
    buildingNumber: '',
    postcode: '',
    town: '',
  },
  dateOfBirth: '',
};

const AddDirector = ({
  onSubmit,
  onClickAdd,
  showAdding = false,
  loading = false,
}: {
  onSubmit: (values: typeof DIRECTOR_INTIAL_VALUES) => void;
  onClickAdd: () => void;
  showAdding: boolean;
  loading: boolean;
}) => {
  if (!showAdding)
    return (
      <div>
        <Button
          variant="tertiary"
          className="mt-m !px-0 sm:w-full"
          onClick={() => onClickAdd()}
        >
          Add major shareholder
          <img className="ml-s" src={PlusIcon} alt="Plus icon" />
        </Button>
      </div>
    );

  return (
    <div className="mt-[28px] border-t border-structure-80">
      <h3>Add a major shareholder</h3>
      <AddDirectorForm onSubmit={onSubmit} loading={loading} />
    </div>
  );
};

const AddDirectorForm = ({
  onSubmit,
  loading,
}: {
  onSubmit: (values: typeof DIRECTOR_INTIAL_VALUES) => void;
  loading: boolean;
}) => {
  const methods = useForm({
    defaultValues: DIRECTOR_INTIAL_VALUES,
    resolver: yupResolver(shareholderValidator),
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          onSubmit as Parameters<typeof methods.handleSubmit>[0],
        )}
      >
        <HookFormDropDown name="title" labelText="Title">
          <TitleOptions />
        </HookFormDropDown>
        <HookFormInput name="firstName" label="First name" type="text" />
        <HookFormInput name="lastName" label="Last name" type="text" />
        <HookFormInput
          name="dateOfBirth"
          label="Date of birth (DD/MM/YYYY)"
          placeholder="DD/MM/YYYY"
          formatter={formatDate}
          inputMode="numeric"
        />
        <AddressInput />
        <WhiteLabelOverride componentType="button" disabled={false}>
          <Button loading={loading} className="sm:w-full">
            Confirm and add
          </Button>
        </WhiteLabelOverride>
      </form>
    </FormProvider>
  );
};

const AddressInput = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<
    GetPostcodeLookupResponse['data'][number][]
  >([]);
  const { control, setValue, formState } = useFormContext();
  const addressValue = useWatch({ control, name: 'address' });
  const isManualAddress = useWatch({ control, name: 'address.manual' });
  const postcodeError = (
    formState.errors.address as { postcode?: { message?: string } } | undefined
  )?.postcode?.message;
  const isPostcodeTouched = (
    formState.touchedFields.address as { postcode?: boolean } | undefined
  )?.postcode;

  useEffect(() => {
    const postcodeSearch = setTimeout(async () => {
      if (searchQuery) {
        try {
          const response = await fetchGetPostcodeLookup({
            query: { postcode: searchQuery },
          });
          setAddressOptions(response.data);
        } catch {
          setAddressOptions([]);
        }
      } else {
        setAddressOptions([]);
      }
    }, 200);

    return () => {
      clearTimeout(postcodeSearch);
    };
  }, [searchQuery]);

  return (
    <div className="mb-l">
      <Combobox
        value={addressValue}
        onChange={(address: GetPostcodeLookupResponse['data'][number]) => {
          setValue(
            'address',
            {
              buildingNumber: address.house_number,
              streetName: address.street_line_1,
              town: address.town,
              postcode: address.postcode,
              buildingName: address.house_name,
              equifaxToken: address.ptc_abs_code,
            },
            { shouldValidate: true, shouldDirty: true },
          );
          setValue('address.manual', false);
        }}
      >
        <div className="relative mb-l flex flex-col">
          <div>
            <Combobox.Input
              onChange={(event) => {
                setSearchQuery(event.target.value);
              }}
              as={Fragment}
            >
              <OrionInput
                name="address.postcode"
                placeholder="Search postcode..."
                className="fs-mask"
                label="Home postcode"
                iconLeft={<IconZoomOutline />}
              />
            </Combobox.Input>
          </div>
          {!isManualAddress && isPostcodeTouched && postcodeError && (
            <span>{postcodeError}</span>
          )}
          {searchQuery && (
            <Combobox.Options
              className="absolute top-full z-[2] m-0 max-h-[200px] w-full list-none overflow-y-auto rounded-s border border-[rgba(0,0,0,0.16)] bg-white p-0 text-structure-40"
              as="ul"
            >
              {addressOptions.length > 0 ? (
                addressOptions.map((address, index) => {
                  const addressName = [
                    address.house_name,
                    address.house_number,
                    address.street_line_1,
                  ]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <Combobox.Option
                      className={({ active }) =>
                        clsx('fs-mask px-l py-s', {
                          'cursor-pointer bg-secondary-97': active,
                        })
                      }
                      key={index}
                      value={address}
                      as="li"
                    >
                      {addressName}
                    </Combobox.Option>
                  );
                })
              ) : (
                <p className="m-0 px-l py-s">No results</p>
              )}
            </Combobox.Options>
          )}
        </div>
      </Combobox>
      {!isManualAddress &&
        (addressValue?.postcode ? (
          <>
            <div className="fs-mask pb-r pt-s">
              <div>
                {addressValue.buildingName} {addressValue.buildingNumber}{' '}
                {addressValue.streetName}
              </div>
              <div>{addressValue.town}</div>
              <div>{addressValue.postcode}</div>
            </div>
            <InlineButton onClick={() => setValue('address.manual', true)}>
              Edit address
            </InlineButton>
          </>
        ) : (
          <InlineButton
            onClick={() => {
              setValue('address', {
                ...addressValue,
                equifaxToken: '',
              });
              setValue('address.manual', !isManualAddress);
            }}
          >
            Enter address manually
          </InlineButton>
        ))}
      {isManualAddress && (
        <div>
          <HookFormInput
            label="Building number"
            name="address.buildingNumber"
            inputMode="numeric"
          />
          <HookFormInput
            label="Street name"
            name="address.streetName"
            type="text"
          />
          <HookFormInput label="Town" name="address.town" type="text" />
          <HookFormInput label="Postcode" name="address.postcode" type="text" />
        </div>
      )}
    </div>
  );
};
